import { IFilter } from '@epicuro-next/ataraxia/models';
import { getFilterValueLabel } from '@epicuro-next/constants/features/localization';
import {
  ProductCardConfiguration,
  ProductCardConfigurationKeys,
  PRODUCT_CARD_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/product-card-rules';
import { capitalizeFirstLetter } from '@epicuro-next/data/product';
import { FilterQueryParams } from '@epicuro-next/models/products';
import {
  FilterPillSetting,
  getValueFromConfigState,
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys,
  PBasketConfigurations,
  pBasketConfigurationsDefaultValues,
  selectConfigurationsState,
  selectConfigurationValueFrom,
  selectShowMarkupEnabled,
} from '@epicuro-next/platform/configuration';
import {
  selectCurrentDateFormat,
  selectLanguage,
} from '@epicuro-next/platform/i18n';
import {
  ensureArray,
  formatDateToLocaleDateUtc,
  intervalISODateToDateRange,
  isDateRangeTypeFilter,
  memoize,
} from '@epicuro-next/utilities';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Filter,
  FilterType,
  FilterValue,
  FilterValueUnion,
  SearchSortType,
  SortingDirection,
} from 'graphql-api/gql-api-interfaces';

import { ExtendedFilterQueryParams } from '../model';

import {
  selectFiltersOrderRule,
  selectHiddenFilterIds,
  selectHiddenFilterValues,
  selectHideMarkupRule,
} from './product-basket-rule.selectors';
import {
  denormalizeStaticFiltersFromFiltersAndActiveFilters,
  isdDatesFilterId,
  normalizeQueryFiltersToActiveFilters,
  orderedProductsBrandIdUrlParam,
  PBASKET_CATALOG_ONLY_MODE_VALUE_KEY,
  PBASKET_FILTERS_VISIBLE_VALUE_KEY as PBASKET_FILTERS_VISIBLE_VALUE_KEY,
  PBASKET_HORIZONTAL_VIEW_VALUE_KEY,
  presetDefaultValue,
  presetFilterId,
  PRODUCTBASKET_FEATURE_KEY,
  State,
  wspFilterId,
} from './product-basket.reducer';

/**
 * Feature selector
 */
export const selectProductBasketState = createFeatureSelector<State>(
  PRODUCTBASKET_FEATURE_KEY,
);

/**
 * Returns the loading state of the filters in basket
 */
export const selectProductBasketFiltersLoaded = createSelector(
  selectProductBasketState,
  (state: State) => state.filtersLoaded,
);

/**
 * Returns the loading state of the next page in basket
 */
export const selectProductBasketNewPageLoaded = createSelector(
  selectProductBasketState,
  (state: State) => state.newPageLoaded,
);

/**
 * Returns the list of products
 */
export const selectProducts = createSelector(
  selectProductBasketState,
  (state: State) => state.products,
);

/**
 * Returns the query filters
 */
export const selectFilterQuery = createSelector(
  selectProductBasketState,
  (state: State) => state.filterQuery,
);

/**
 * Returns the product basket facet query
 */
export const selectProductBasketFacetQuery = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.f,
);

/**
 * Returns the product basket search query
 */
export const selectProductBasketSearchQuery = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.q,
);

/**
 * Returns the list of filters
 */
export const selectFilters = createSelector(
  selectProductBasketState,
  (state: State) => state.filters,
);

/**
 * Returns the list of filters
 */
export const selectAvailableFilters = createSelector(
  selectFilters,
  selectFiltersOrderRule,
  sortFilters,
);

export const selectDefaultFilters = createSelector(
  selectProductBasketState,
  (state: State) => state.defaultFilters,
);

export const selectDefaultSortedFilters = createSelector(
  selectDefaultFilters,
  selectFiltersOrderRule,
  sortFilters,
);

export const selectAvailableFiltersWithDisabled = createSelector(
  selectAvailableFilters,
  selectDefaultSortedFilters,
  (availableFilters, defaultFilters) =>
    defaultFilters.map((defaultFilter) => {
      const fetchedFilter = availableFilters.find(
        ({ fieldId }) => defaultFilter.fieldId === fieldId,
      );
      const missingValues =
        fetchedFilter?.values?.filter(
          ({ value }) =>
            !defaultFilter.values?.find(
              (filterValue) => filterValue.value === value,
            ),
        ) ?? [];
      return {
        ...defaultFilter,
        values: [
          ...(defaultFilter.values?.map((filterValue) => {
            const availableValue = fetchedFilter?.values?.find(
              ({ value }) => filterValue.value === value,
            );
            return {
              ...filterValue,
              disabled: !availableValue,
              count:
                availableValue?.count === undefined ? 0 : availableValue?.count,
            };
          }) as FilterValue[]),
          ...missingValues,
        ],
      };
    }),
);

export const selectShowEmptyFilters = createSelector(
  selectConfigurationsState,
  (configState) =>
    getValueFromConfigState<
      PBasketConfigurationKeys.ShowEmptyFilterOptions,
      PBasketConfigurations
    >(
      configState,
      NAMESPACE_PRODUCT_BASKET,
      PBasketConfigurationKeys.ShowEmptyFilterOptions,
      pBasketConfigurationsDefaultValues[
        PBasketConfigurationKeys.ShowEmptyFilterOptions
      ],
    ),
);

export const selectHideEmptyFilterValues = createSelector(
  selectConfigurationsState,
  (configState) =>
    getValueFromConfigState<
      PBasketConfigurationKeys.HideEmptyFilterValues,
      PBasketConfigurations
    >(
      configState,
      NAMESPACE_PRODUCT_BASKET,
      PBasketConfigurationKeys.HideEmptyFilterValues,
      false,
    ),
);

export const selectISDSelectableDaysInThePast = selectConfigurationValueFrom<
  PBasketConfigurationKeys.ISDSelectableDaysInThePast,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.ISDSelectableDaysInThePast,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.ISDSelectableDaysInThePast
  ],
);

const selectFilterPillsSetting = selectConfigurationValueFrom<
  PBasketConfigurationKeys.FilterPillsSetting,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.FilterPillsSetting,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.FilterPillsSetting
  ],
);

export const selectGroupByVariantAvailable = selectConfigurationValueFrom<
  PBasketConfigurationKeys.GroupByVariantAvailable,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.GroupByVariantAvailable,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.GroupByVariantAvailable
  ],
);

export const selectFilterIcons = selectConfigurationValueFrom<
  PBasketConfigurationKeys.FilterIcons,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.FilterIcons,
  pBasketConfigurationsDefaultValues[PBasketConfigurationKeys.FilterIcons],
);
export const selectFilterIconsParsed = createSelector(
  selectFilterIcons,
  (value: string) =>
    JSON.parse(value) as {
      [key: string]: { [key: string]: string };
    },
);
export const selectFilterPillsSettingParsed = createSelector(
  selectFilterPillsSetting,
  (filterPillsSetting: string) => {
    return JSON.parse(filterPillsSetting) as FilterPillSetting[];
  },
);

export const selectFilteredOrDefaultFilters = createSelector(
  selectAvailableFilters,
  selectAvailableFiltersWithDisabled,
  selectShowEmptyFilters,
  selectHideEmptyFilterValues,
  selectHiddenFilterIds,
  selectHiddenFilterValues,
  (
    availableFilters,
    availableFiltersWithDisabled,
    showEmptyFilters,
    hideEmptyFilterValues,
    hiddenFilterIds,
    hiddenFilterValues,
  ) => {
    let result: Filter[] = [];
    if (showEmptyFilters) {
      if (hideEmptyFilterValues) {
        result = filterOutEmptyFilterValues(availableFiltersWithDisabled);
      } else {
        result = availableFiltersWithDisabled;
      }
    } else {
      result = availableFilters;
    }
    return result
      .filter((filter) => !hiddenFilterIds.includes(filter.fieldId))
      .map((filter) => {
        const hiddenValues = ensureArray(hiddenFilterValues[filter.fieldId]);
        return hiddenValues.length > 0
          ? {
              ...filter,
              values: filter.values?.filter(
                ({ value }) => !hiddenValues.includes(value),
              ),
            }
          : filter;
      });
  },
);

/**
 * Returns the preset filter value from the query url
 */
export const selectPresetValue = createSelector(
  selectProductBasketState,
  (state: State) => state.currentPresetValue,
);

/**
 * Returns the list of active filters
 */
export const selectActiveFilters = createSelector(
  selectFilterQuery,
  selectPresetValue,
  (filterQuery: FilterQueryParams, presetValue: string): IFilter[] =>
    normalizeQueryFiltersToActiveFilters(filterQuery, presetValue),
);

export const selectActiveFiltersCount = createSelector(
  selectActiveFilters,
  selectAvailableFilters,
  selectHiddenFilterIds,
  (activeFilters, availableFilters, hiddenFilterIds) => {
    const mergedFields = availableFilters
      .filter((field) => field.mergeValues)
      .map((filter) => filter.fieldId);

    return activeFilters
      .filter(({ fieldId }) => !hiddenFilterIds.includes(fieldId))
      .map((activeFilter) => ({
        ...activeFilter,
        isMerged: mergedFields.includes(activeFilter.fieldId),
      }))
      .reduce((acc: IFilter[], curr) => {
        if (
          curr.isMerged &&
          acc.find((item) => item.fieldId === curr.fieldId)
        ) {
          return acc;
        }

        return [...acc, curr];
      }, []).length;
  },
);

export const selectAvailableAndActiveValues = createSelector(
  selectFilteredOrDefaultFilters,
  selectActiveFilters,
  (availableFilters: Filter[], activeFilters: IFilter[]): Filter[] => {
    const mergedFilters: Filter[] = availableFilters.map((f) => ({
      ...f,
      values: [...(f.values ?? [])],
    }));

    for (const active of activeFilters) {
      const { fieldId, value, dataType } = active;

      const filter = mergedFilters.find((f) => f.fieldId === fieldId);

      const newValue: FilterValueUnion = {
        value,
        displayName: value,
        count: null,
        type: FilterType.Value,
      };

      if (filter) {
        const alreadyIncluded = filter.values?.some((v) => v.value === value);
        if (!alreadyIncluded) {
          // tslint:disable-next-line:no-non-null-assertion
          filter.values!.push(newValue);
        }
      } else {
        // Inject with no displayName so the template hides the title
        mergedFilters.push({
          fieldId,
          displayName: '',
          displayValueTransform: null,
          isMultiple: true,
          isFacet: false,
          mergeValues: null,
          order: 999, // Place at the end
          type: FilterType.Value,
          values: [newValue],
          dataType: dataType ?? null,
          maxAmountOfValues: null,
          emptyInitialValues: null,
          collapse: {
            enabled: false,
            opened: null,
            linkedFields: null,
          },
          subFilters: null,
          translationKey: null,
        });
      }
    }

    return mergedFilters;
  },
);

export const selectFirstActiveDisplayValueOf = memoize((fieldId: string) =>
  createSelector(
    selectActiveFilters,
    selectAvailableFilters,
    (activeFilters: IFilter[], availableFilters: Filter[]) => {
      const firstMatchingFilter = activeFilters.find(
        (filter) => filter.fieldId === fieldId,
      );

      if (firstMatchingFilter === undefined) {
        return;
      }

      return availableFilters
        .find((availableFilter) => availableFilter.fieldId === fieldId)
        ?.values?.find((value) => value.value === firstMatchingFilter.value)
        ?.displayName;
    },
  ),
);

export const selectLastActivatedFilter = createSelector(
  selectProductBasketState,
  (state: State) => state.lastActivatedFilter?.fieldId,
);

/**
 * Returns the preset filter default value
 */
export const selectPresetFilterDefaultValue = createSelector(
  selectProductBasketState,
  (state) => state.ruleEnginePresetValue ?? presetDefaultValue,
);

/**
 * Returns the list of active filters aggregated by value
 */
export const selectAggregatedActiveFilters = createSelector(
  selectAvailableFilters,
  selectActiveFilters,
  selectLanguage,
  selectCurrentDateFormat,
  selectHiddenFilterIds,
  (
    availableFilters: Filter[],
    activeFilters: IFilter[],
    locale: string,
    dateFormat: string,
    hiddenFilterIds: string[],
  ) => {
    const dictionary = activeFilters
      .filter(({ fieldId }) => !hiddenFilterIds.includes(fieldId))
      .reduce(
        (
          aggr: {
            [key: string]: {
              values: string[];
              originFilter?: Filter;
            };
          },
          filter,
        ) => {
          const originFilter = availableFilters.find(
            (entry) => entry.fieldId === filter.fieldId,
          );
          const originValue = originFilter?.values?.find((entry) =>
            originFilter.mergeValues
              ? entry.value.includes(filter.value)
              : entry.value === filter.value,
          );
          let currentValues = aggr[filter.fieldId]
            ? aggr[filter.fieldId].values
            : [];

          if (filter.fieldId === presetFilterId) {
            currentValues.push(
              getFilterValueLabel(filter.value) ?? filter.value,
            );
          } else if (originValue) {
            currentValues.push(originValue.displayName);
          } else {
            currentValues.push(filter.value);
          }

          if (originFilter?.mergeValues && originValue) {
            currentValues = [originValue.displayName];
          }

          return currentValues.length > 0
            ? {
                ...aggr,
                [filter.fieldId]: {
                  values: currentValues,
                  originFilter,
                },
              }
            : aggr;
        },
        {},
      );

    return Object.entries(dictionary).map((entry) => {
      let values = entry[1].values;
      const originFilter = entry[1].originFilter;
      const fieldId = entry[0];
      if (fieldId === 'availability') {
        values = values.map((date) => {
          const fDate = formatDateToLocaleDateUtc(
            new Date(date),
            locale,
            'MMM yy',
          );
          return capitalizeFirstLetter(fDate);
        });
        // Delete check on filter id after switch to search v2
      } else if (
        fieldId === isdDatesFilterId ||
        isDateRangeTypeFilter(originFilter)
      ) {
        values = values.map((date) => {
          const { from, to } = intervalISODateToDateRange(date);
          const formattedFrom = formatDateToLocaleDateUtc(
            new Date(from),
            locale,
            dateFormat,
          );
          const formattedTo = formatDateToLocaleDateUtc(
            new Date(to as string),
            locale,
            dateFormat,
          );
          return `${formattedFrom} - ${formattedTo}`;
        });
      }

      return {
        fieldId,
        value: values.join(', '),
        originFilter: entry[1].originFilter,
      };
    });
  },
);

/**
 * Returns the list of static filters (not facet)
 */
export const selectStaticFilters = createSelector(
  selectAvailableFilters,
  selectActiveFilters,
  (availableFilters: Filter[], activeFilters: IFilter[]) =>
    denormalizeStaticFiltersFromFiltersAndActiveFilters(
      availableFilters,
      activeFilters,
    ),
);

export const selectActiveWspFilter = createSelector(
  selectStaticFilters,
  (staticFilter) => staticFilter[wspFilterId] ?? '',
);

/**
 * Returns the page number
 */
export const selectProductBasketPageNumber = createSelector(
  selectProductBasketState,
  (state: State) => state.page,
);

/**
 * Returns the page size
 */
export const selectProductBasketPageSize = createSelector(
  selectProductBasketState,
  (state: State) => state.size,
);

/**
 * Returns the sort field
 */
export const selectSortField = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.sortField,
);

/**
 * Returns the orderHistoryToDate
 */
export const selectOrderHistoryFromDate = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.orderHistoryFromDate,
);

/**
 * Returns the orderHistoryToDate
 */
export const selectOrderHistoryToDate = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.orderHistoryToDate,
);

export const selectOrderedProductsBrandId = createSelector(
  selectProductBasketState,
  (state) => state.orderedProductsBrandId,
);

/**
 * Returns the preset filter displayName
 */
export const selectPresetDisplayName = createSelector(
  selectPresetValue,
  selectAvailableFilters,
  (presetValue: string, availableFilters: Filter[]) => {
    const label = getFilterValueLabel(presetValue);
    if (label) {
      return label;
    }

    return (
      availableFilters
        .find((filter) => filter.fieldId === presetFilterId)
        ?.values?.find(({ value }) => presetValue === value)?.displayName ??
      null
    );
  },
);

/**
 * Returns the sort direction
 */
export const selectSortDirection = createSelector(
  selectFilterQuery,
  (filterQuery: FilterQueryParams) => filterQuery.sortDirection,
);

export const selectCollapse = createSelector(
  selectProductBasketState,
  (state) => state.collapse,
);

export const selectPaginationParams = createSelector(
  selectProductBasketPageNumber,
  selectProductBasketPageSize,
  (page: number, pageSize: number) => ({ page, pageSize }),
);

export const selectProductBasketQueryParams = createSelector(
  selectPresetValue,
  selectProductBasketSearchQuery,
  selectProductBasketFacetQuery,
  selectStaticFilters,
  selectPaginationParams,
  selectSortField,
  selectSortDirection,
  selectCollapse,
  (
    presetValue: string,
    query: string,
    facets: string,
    staticFilters: { [key: string]: string },
    paginationParams: { page: number; pageSize: number },
    sortField: SearchSortType,
    sortDirection: SortingDirection,
    collapse: boolean,
  ): ExtendedFilterQueryParams => {
    return {
      q: query,
      f: facets,
      start: paginationParams.page * paginationParams.pageSize,
      num: paginationParams.pageSize,
      preset: presetValue,
      subsites: staticFilters.subsite,
      availability: staticFilters.availability,
      maxAge: staticFilters.maxAge,
      rrp: staticFilters.retailPriceRange,
      wsp: staticFilters.wholesalePriceRange,
      sortField,
      sortDirection,
      collapse,
      inventoryRange: staticFilters.inventoryRange,
      isdDates: staticFilters.isdDates,
      filterOutInbound: staticFilters.filterOutInbound,
      filterOutRecentlyDelivered: staticFilters.filterOutRecentlyDelivered,
    };
  },
);

export const selectProductBasketQueryParamsExtended = createSelector(
  selectProductBasketQueryParams,
  selectGroupByVariantAvailable,
  selectOrderHistoryFromDate,
  selectOrderHistoryToDate,
  selectOrderedProductsBrandId,
  (
    productBasketQueryParams,
    groupByVariantAvailable,
    orderHistoryFromDate,
    orderHistoryToDate,
    orderedProductsBrandId,
  ): ExtendedFilterQueryParams => {
    return {
      ...productBasketQueryParams,
      withRelatedVariants: !(
        groupByVariantAvailable && !productBasketQueryParams.collapse
      ),
      orderHistoryFromDate,
      orderHistoryToDate,
      [orderedProductsBrandIdUrlParam]: orderedProductsBrandId,
    };
  },
);

export const selectSearchSuggestDefaultProductBasketQueryParams =
  createSelector(selectProductBasketQueryParamsExtended, (params) => {
    const {
      preset,
      sortDirection,
      sortField,
      subsites,
      withRelatedVariants,
      collapse,
    } = params;
    return {
      preset,
      sortDirection,
      sortField,
      subsites,
      withRelatedVariants,
      collapse,
    };
  });

export const selectFilteredProductBasketQueryParams = createSelector(
  selectProductBasketQueryParams,
  (params: ExtendedFilterQueryParams) => {
    const { start, num, site, q, f, subsites, preset, siteId, ...restParams } =
      params;

    const data = {
      ...restParams,
      ...(q ? { q } : {}),
      ...(f ? { f } : {}),
      ...(subsites ? { subsites } : {}),
      ...(preset ? { preset } : {}),
    };

    return Object.entries(data)
      .filter(([, value]: [string, unknown]) => value !== undefined)
      .reduce(
        (obj: Record<string, unknown>, [key, value]: [string, unknown]) => {
          obj[key] = value;
          return obj;
        },
        {},
      );
  },
);

/**
 * Returns the list of sorting options
 */
export const selectSorting = createSelector(
  selectProductBasketState,
  (state: State) => state.sorting,
);

/**
 * Returns the basket products load error (if any)
 */
export const selectProductLoadError = createSelector(
  selectProductBasketState,
  (state: State) => state.productLoadError,
);

export const selectProductsLoading = createSelector(
  selectProductBasketState,
  (state: State) => state.productsLoading,
);

/**
 * Returns the basket filters load error (if any)
 */
export const selectFilterLoadError = createSelector(
  selectProductBasketState,
  (state: State) => state.filterLoadError,
);

/**
 * Returns the search result count
 */
export const selectResultCount = createSelector(
  selectProductBasketState,
  (state: State) => state.resultCount,
);

/**
 * Returns if there is remaining product not loaded
 */
export const selectIsRemainingProductsNotLoaded = createSelector(
  selectProducts,
  selectResultCount,
  (products, resultCount) => products.length < resultCount,
);

export const selectNoProducts = createSelector(
  selectProductsLoading,
  selectResultCount,
  (loading, resultCount) => !loading && resultCount === 0,
);

/**
 * Returns if all products are loaded
 */
export const selectAllProductsLoaded = createSelector(
  selectIsRemainingProductsNotLoaded,
  selectResultCount,
  (isRemainingProductsNotLoaded, resultCount) =>
    !isRemainingProductsNotLoaded && resultCount > 0,
);

export const selectCatalogOnlyModeEnabledSettingValue = createSelector(
  selectProductBasketState,
  (state) => !!state[PBASKET_CATALOG_ONLY_MODE_VALUE_KEY],
);

export const selectHorizontalViewEnabledSettingValue = createSelector(
  selectProductBasketState,
  (state) => !!state[PBASKET_HORIZONTAL_VIEW_VALUE_KEY],
);

export const selectFiltersVisibleSettingValue = createSelector(
  selectProductBasketState,
  (state) => !!state[PBASKET_FILTERS_VISIBLE_VALUE_KEY],
);

export const selectExportSearchResult = createSelector(
  selectProductBasketState,
  (state) => state.exportSearchResult,
);

export const selectIsExportSearchResultLoading = createSelector(
  selectExportSearchResult,
  (exportResult) => exportResult.loading,
);

export const selectDefaultSortingConfig = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.DefaultSortingConfig,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.DefaultSortingConfig,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.DefaultSortingConfig
    ],
  ),
  (config: string) => {
    try {
      return JSON.parse(config);
    } catch {
      return {
        field: SearchSortType.Default,
        direction: SortingDirection.Desc,
      };
    }
  },
);

export const selectProductTitlePrefixFormat = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.ProductTitlePrefixFormat,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.ProductTitlePrefixFormat,
  [],
);

export const selectShowEstimatedDeliveryDate = createSelector(
  selectPresetValue,
  selectConfigurationValueFrom<
    ProductCardConfigurationKeys.DeliveryDateVisibility,
    ProductCardConfiguration
  >(
    PRODUCT_CARD_FEATURE_NAME,
    ProductCardConfigurationKeys.DeliveryDateVisibility,
    false,
  ),
  (preset: string, deliveryDateVisibility: string | boolean) => {
    if (typeof deliveryDateVisibility === 'boolean') {
      return deliveryDateVisibility;
    }

    try {
      const parsed: Record<string, boolean> = JSON.parse(
        deliveryDateVisibility as string,
      );

      const result = parsed[preset];

      return typeof result === 'undefined' ? false : !!result;
    } catch {
      return false;
    }
  },
);

export const selectExpandingProductCard = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ExpandingProductCard,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ExpandingProductCard,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ExpandingProductCard
    ],
  ),
  (config: string) => {
    try {
      return JSON.parse(config);
    } catch {
      console.error('Failed to parse expanding product cards config', config);
      return {
        active: false,
        content: ['Information'],
        randomizeContent: false,
        footerButtonAttributes: ['productdivision', 'category'],
      };
    }
  },
);

export const selectExpandingProductCardActive = createSelector(
  selectExpandingProductCard,
  (expandingProductCardSetting) => expandingProductCardSetting.active,
);

export const selectExpandingProductCardContent = createSelector(
  selectExpandingProductCard,
  (expandingProductCardSetting) => expandingProductCardSetting.content,
);

export const selectExpandingProductCardFooterButtonAttributes = createSelector(
  selectExpandingProductCard,
  (expandingProductCardSetting) =>
    expandingProductCardSetting.footerButtonAttributes,
);

export const selectExpandingProductCardRandomizeContent = createSelector(
  selectExpandingProductCard,
  (expandingProductCardSetting) => expandingProductCardSetting.randomizeContent,
);

export function filterOutEmptyFilterValues(filters: Filter[]) {
  return filters.map((filter) =>
    filter.isFacet
      ? {
          ...filter,
          values: filter.values?.filter((filterValue) => filterValue.count),
        }
      : filter,
  );
}

export const selectShowPCardMarkup = createSelector(
  selectShowMarkupEnabled,
  selectHideMarkupRule,
  (globalRule, hidePrice) => globalRule && !hidePrice,
);

export const selectMultiRangeSliderInputsRule = selectConfigurationValueFrom<
  PBasketConfigurationKeys.MultiRangeSliderInputs,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.MultiRangeSliderInputs,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.MultiRangeSliderInputs
  ],
);

export const selectShowMediaRelationsRule = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ShowMediaRelations,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ShowMediaRelations,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ShowMediaRelations
    ],
  ),
  (config: string) => {
    try {
      return JSON.parse(config);
    } catch {
      console.warn('Failed to parse media relations config', config);
      return undefined;
    }
  },
);

export const selectMediaRelationsMediaCount = createSelector(
  selectShowMediaRelationsRule,
  (showMediaRelationsRule) => showMediaRelationsRule?.mediaCount,
);

export const selectMediaRelationsLimit = createSelector(
  selectShowMediaRelationsRule,
  (showMediaRelationsRule) => showMediaRelationsRule?.limit,
);

export const selectExpandingUnavailableProductCardEnabled =
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ExpandingUnavailableProductCard,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ExpandingUnavailableProductCard,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ExpandingUnavailableProductCard
    ],
  );

export const selectHideProductsTitle = selectConfigurationValueFrom<
  PBasketConfigurationKeys.HideProductsTitle,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.HideProductsTitle,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.HideProductsTitle
  ],
);

export const selectExpandingCardAttributes = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ExpandingCardAttributes,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ExpandingCardAttributes,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ExpandingCardAttributes
    ],
  ),
  (config: string) => {
    try {
      return JSON.parse(config);
    } catch {
      console.error('Failed to parse expanding card attributes config', config);
      return undefined;
    }
  },
);

export const selectStackByFilters = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.StackByFilters,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.StackByFilters,
    pBasketConfigurationsDefaultValues[PBasketConfigurationKeys.StackByFilters],
  ),
  (stackByFiltersConfig: string) => {
    try {
      return JSON.parse(stackByFiltersConfig);
    } catch {
      console.error(
        'Failed to parse stack by filters config',
        stackByFiltersConfig,
      );
      return undefined;
    }
  },
);

export const selectShortcutFilters = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ShortcutFilters,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ShortcutFilters,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ShortcutFilters
    ],
  ),
  (shortcutFiltersConfig: string) => {
    try {
      return JSON.parse(shortcutFiltersConfig);
    } catch {
      console.error(
        'Failed to parse shortcut filters config',
        shortcutFiltersConfig,
      );
      return undefined;
    }
  },
);

export const selectSearchBarSegmentButton = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.SearchBarSegmentButton,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.SearchBarSegmentButton,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.SearchBarSegmentButton
    ],
  ),
  // tslint:disable-next-line:no-identical-functions
  (stackByFiltersConfig: string) => {
    try {
      return JSON.parse(stackByFiltersConfig);
    } catch {
      console.error(
        'Failed to parse search bar segment buttons config',
        stackByFiltersConfig,
      );
      return undefined;
    }
  },
);

function sortFilters(filters: Filter[], filtersOrder: string[]) {
  return filtersOrder.length
    ? [...filters].sort(
        (a, b) =>
          filtersOrder.indexOf(a.fieldId) - filtersOrder.indexOf(b.fieldId),
      )
    : filters;
}
